import React from "react";
import { useNavigate } from "react-router-dom";
import { fileUrl } from "@/lib/api";
import { money, merchCategoryLabel, tierRank, TIER_LABELS } from "@/lib/format";
import { useCart } from "@/context/CartContext";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { ShoppingBag, Lock, Crown } from "lucide-react";
import { toast } from "sonner";

export default function MerchCard({ product }) {
  const navigate = useNavigate();
  const { addItem, setOpen } = useCart();
  const { customer } = useCustomerAuth();
  const photo = product.images?.[0]?.url;
  const soldOut = product.in_stock === false;
  const locked =
    product.vip_only && (!customer || tierRank(customer.vip_tier) < tierRank(product.vip_min_tier));

  const open = () => navigate(`/shop/${product.slug}`);

  const add = (e) => {
    e.stopPropagation();
    if (locked) {
      navigate("/vip");
      return;
    }
    addItem(product, 1);
    setOpen(true);
    toast.success(`${product.name} added to cart`);
  };

  return (
    <div
      onClick={open}
      data-testid={`merch-card-${product.id}`}
      className="group text-left bg-[var(--panel)] border border-[var(--border-lch)] rounded-lg overflow-hidden hover:border-[#c9a86a]/50 transition-colors duration-300 cursor-pointer flex flex-col"
    >
      <div className="relative aspect-square bg-[var(--panel-2)] overflow-hidden">
        {photo ? (
          <img src={fileUrl(photo)} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[var(--muted)]"><ShoppingBag size={36} /></div>
        )}
        <div className="absolute top-3 left-3 flex flex-col gap-1.5">
          {product.featured && <span className="text-[10px] px-2 py-1 rounded-full bg-[#c9a86a] text-[var(--on-gold)] tracking-wide font-semibold">FEATURED</span>}
          {product.vip_only && <span className="text-[10px] px-2 py-1 rounded-full bg-[var(--panel-2)] text-[#c9a86a] border border-[#c9a86a]/40 tracking-wide flex items-center gap-1"><Crown size={10} /> VIP</span>}
        </div>
        {product.low_stock && !soldOut && (
          <div className="absolute top-3 right-3 text-[10px] px-2 py-1 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30 tracking-wide">LOW STOCK</div>
        )}
        {soldOut && (
          <div className="absolute inset-0 bg-black/55 flex items-center justify-center"><span className="text-[var(--text)] tracking-[0.2em] text-sm">SOLD OUT</span></div>
        )}
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <div className="text-[11px] text-[var(--muted)] tracking-wide uppercase">{merchCategoryLabel(product.category)}</div>
        <div className="text-[var(--text)] font-medium mt-0.5 truncate">{product.name}</div>
        <div className="mt-2 flex items-center gap-2">
          <span className="text-[#c9a86a] font-medium">{money(product.price)}</span>
          {product.compare_at_price && product.compare_at_price > product.price && (
            <span className="text-xs text-[var(--muted)] line-through">{money(product.compare_at_price)}</span>
          )}
        </div>
        <div className="mt-3">
          {locked ? (
            <button onClick={add} data-testid={`merch-vip-${product.id}`} className="w-full flex items-center justify-center gap-1.5 text-xs py-2 border border-[#c9a86a]/40 text-[#c9a86a] rounded hover:bg-[var(--panel-2)] transition-colors">
              <Lock size={12} /> VIP Exclusive · {TIER_LABELS[product.vip_min_tier] || "Members"}
            </button>
          ) : (
            <button onClick={add} disabled={soldOut} data-testid={`merch-add-${product.id}`} className="w-full text-xs py-2 bg-[var(--panel-2)] text-[var(--text)] rounded hover:bg-[var(--border-2)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-1.5">
              <ShoppingBag size={13} /> {soldOut ? "Sold Out" : "Add to Cart"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
